import Spinner from 'react-bootstrap/Spinner';
import {
    FiKey, FiEye, FiEyeOff, FiCopy, FiCheck,
    FiExternalLink, FiShield, FiAlertCircle, FiHash, FiLock,
} from 'react-icons/fi';

import { useEffect } from "react";
import { useState, useRef } from 'react';
import { useAuth } from '../lib/StAuthGiver.js';
import { callApi, getCurSiteId } from '../lib/Common_funcs.js';

const WhichEnv = (process.env.REACT_APP_TURBO_ENV === undefined ? 'aws' : 'staging');
const Tdebug = (WhichEnv != 'aws');

export const StAuthKeys = () => {
    const [loading, setLoading] = useState(true);
    const { loginAction, logOut } = useAuth();
    const [apiKey, setApiKey] = useState(false);
    const [showKey, setShowKey] = useState(false);
    const isMounted = useRef(false);
    const [pubAcctSid, setPubAcctSid] = useState(false);
    const [copyMessage, setCopyMessage] = useState(false);
    const copyTarget = useRef(null);
    const [acctSid, setAcctSid] = useState('');
    const [siteId, setSiteid] = useState(getCurSiteId());
    const [act_e, setAct_E] = useState('');

    // Security Credentials card (values from the API)
    const [credSiteId, setCredSiteId] = useState('');
    const [credAccountSid, setCredAccountSid] = useState('');
    const [credApiKey, setCredApiKey] = useState('');
    const [showCredKey, setShowCredKey] = useState(false);
    const [copiedField, setCopiedField] = useState(null);

    const copyValue = async (value, field) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopiedField(field);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    const getApiServer = () => {
        if (WhichEnv === 'aws') {
            return 'https://api-us.turbosteed.com';
        }
        else {
            return `https://${process.env.REACT_APP_HOST_URL}`;
        }
    }

    useEffect(() => {
        if (isMounted.current == false) {
            isMounted.current = true;
        }
        else {
            return;
        }

        const fetchData = async () => {
            const rcJson = await callApi('/sites/' + siteId + '/pubapikey', 'GET', {}, {});
            Tdebug && console.log(rcJson);

            if (rcJson.disposition == 'logout') {
                Tdebug && console.log("Now logging out");
                logOut();
            }

            if (rcJson.httpStatus != 200) {
                if (rcJson.disposition == 'retry') {
                    Tdebug && console.log(rcJson.message);
                }
                else if (rcJson.disposition == 'abort') {
                    Tdebug && console.log(rcJson.message);
                }

                if (rcJson.respBody.etac) {
                    loginAction(null, rcJson.etac);
                }
            }
            else {
                if (rcJson.respBody.etac) {
                    loginAction(null, rcJson.etac);
                }

                Tdebug && console.log("Setting values");
                setApiKey(rcJson.respBody.publicKey);
                setPubAcctSid(rcJson.respBody.publicSiteId);
                setCredSiteId(rcJson.respBody.siteId);
                setCredAccountSid(rcJson.respBody.accountSid);
                setCredApiKey(rcJson.respBody.apiKey);
                setLoading(false);
            }
        }

        fetchData();
    }, [apiKey, pubAcctSid]);

    useEffect(() => {
        Tdebug && console.log("empty use effect for showKey and copyMessage");
    }, [showKey, copyMessage]);

    const handleCopyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(apiKey);
            setCopyMessage(true);
            setTimeout(() => setCopyMessage(false), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    if (loading) {
        return (
            <div className="app-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                <div style={{ textAlign: 'center' }}>
                    <Spinner animation="border" style={{ width: '2rem', height: '2rem', color: 'var(--accent-primary)' }} />
                    <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-tertiary)' }}>Loading...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="app-content animate-in">
            <div className="dashboard-header">
                <h1 className="dashboard-title">Auth Keys</h1>
                <p className="dashboard-subtitle">Manage API keys for your website integrations</p>
            </div>

            {/* Security warning */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: 'var(--warning-soft)', border: '1px solid rgba(245,158,11,0.2)',
                borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: '24px',
                fontSize: '13px', color: 'var(--text-primary)',
            }}>
                <FiAlertCircle size={16} style={{ color: 'var(--warning)', flexShrink: 0 }} />
                Keep your API keys secure. Never share them publicly or commit them to version control.
            </div>

            {/* Key card */}
            <div className="bento-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                    <div className="stat-card-icon"><FiKey size={18} /></div>
                    <div>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>Contact Form Widget Auth Key</h3>
                        <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', margin: 0 }}>API credentials for form integration</p>
                    </div>
                </div>

                {/* Public Account SID */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-primary)' }}>
                        Public Account SID
                    </label>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '10px 12px', background: 'var(--bg-tertiary)',
                        borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)',
                    }}>
                        <FiHash size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                        <code style={{ flex: 1, fontSize: '13px', fontFamily: 'monospace', color: 'var(--text-primary)', wordBreak: 'break-all' }}>
                            {pubAcctSid}
                        </code>
                        <button
                            onClick={() => copyValue(pubAcctSid, 'pubAcctSid')}
                            title="Copy Public Account SID"
                            aria-label="Copy Public Account SID"
                            style={{
                                display: 'flex', alignItems: 'center', gap: '5px',
                                padding: '5px 10px', border: '1px solid var(--border-color)',
                                borderRadius: 'var(--radius-sm)', background: copiedField === 'pubAcctSid' ? 'var(--success-soft)' : 'var(--bg-secondary)',
                                color: copiedField === 'pubAcctSid' ? 'var(--success)' : 'var(--text-primary)',
                                fontSize: '12px', fontWeight: 500, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit',
                            }}
                        >
                            {copiedField === 'pubAcctSid' ? <><FiCheck size={13} /> Copied!</> : <><FiCopy size={13} /> Copy</>}
                        </button>
                    </div>
                </div>

                {/* Public API Key */}
                <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-primary)' }}>
                        Public API Key
                    </label>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)',
                        borderRadius: 'var(--radius-md)', padding: '10px 12px',
                    }}>
                        <FiKey size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                        <code style={{ flex: 1, fontSize: '13px', fontFamily: 'monospace', color: 'var(--text-secondary)', wordBreak: 'break-all' }}>
                            {showKey ? apiKey : '•'.repeat(32)}
                        </code>
                        <button
                            onClick={() => { setShowKey(!showKey); setCopyMessage(false); }}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: '30px', height: '30px', border: 'none', background: 'transparent',
                                color: 'var(--text-secondary)', cursor: 'pointer', borderRadius: 'var(--radius-sm)', flexShrink: 0,
                            }}
                            title={showKey ? 'Hide key' : 'Show key'}
                        >
                            {showKey ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                        </button>
                        {showKey && (
                            <button
                                ref={copyTarget}
                                onClick={handleCopyToClipboard}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '5px',
                                    padding: '5px 10px', border: '1px solid var(--border-color)',
                                    borderRadius: 'var(--radius-sm)', background: copyMessage ? 'var(--success-soft)' : 'var(--bg-secondary)',
                                    color: copyMessage ? 'var(--success)' : 'var(--text-primary)',
                                    fontSize: '12px', fontWeight: 500, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit',
                                }}
                            >
                                {copyMessage ? <><FiCheck size={13} /> Copied!</> : <><FiCopy size={13} /> Copy</>}
                            </button>
                        )}
                    </div>
                </div>

                {/* Integration instructions */}
                <div style={{
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                    gap: '16px', flexWrap: 'wrap',
                    background: 'var(--accent-soft)', border: '1px solid rgba(37,99,235,0.15)',
                    borderRadius: 'var(--radius-md)', padding: '14px 16px',
                }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                        <FiShield size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0, marginTop: '1px' }} />
                        <div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '2px' }}>Integration Instructions</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Use these credentials to integrate contact forms with your website.</div>
                        </div>
                    </div>
                    <a
                        href="https://www.turbosteed.com/blogs/contact-forms"
                        target="_blank" rel="noopener noreferrer"
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            padding: '7px 14px', background: 'var(--accent-primary)', color: 'white',
                            borderRadius: 'var(--radius-md)', fontSize: '13px', fontWeight: 500,
                            textDecoration: 'none', flexShrink: 0,
                        }}
                    >
                        <FiExternalLink size={13} /> View Guide
                    </a>
                </div>
            </div>

            {/* Security Credentials card */}
            <div className="bento-card" style={{ marginTop: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                    <div className="stat-card-icon"><FiShield size={18} /></div>
                    <div>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0 }}>Security Credentials</h3>
                        <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', margin: 0 }}>Sensitive API authentication details</p>
                    </div>
                </div>

                {/* Site ID */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-primary)' }}>
                        Site ID
                    </label>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '10px 12px', background: 'var(--bg-tertiary)',
                        borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)',
                    }}>
                        <FiHash size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                        <code style={{ flex: 1, fontSize: '13px', fontFamily: 'monospace', color: 'var(--text-primary)', wordBreak: 'break-all' }}>
                            {credSiteId}
                        </code>
                        <button
                            onClick={() => copyValue(credSiteId, 'siteId')}
                            title="Copy Site ID"
                            aria-label="Copy Site ID"
                            style={{
                                display: 'flex', alignItems: 'center', gap: '5px',
                                padding: '5px 10px', border: '1px solid var(--border-color)',
                                borderRadius: 'var(--radius-sm)', background: copiedField === 'siteId' ? 'var(--success-soft)' : 'var(--bg-secondary)',
                                color: copiedField === 'siteId' ? 'var(--success)' : 'var(--text-primary)',
                                fontSize: '12px', fontWeight: 500, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit',
                            }}
                        >
                            {copiedField === 'siteId' ? <><FiCheck size={13} /> Copied!</> : <><FiCopy size={13} /> Copy</>}
                        </button>
                    </div>
                </div>

                {/* Account SID */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-primary)' }}>
                        Account SID
                    </label>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '10px 12px', background: 'var(--bg-tertiary)',
                        borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)',
                    }}>
                        <FiHash size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                        <code style={{ flex: 1, fontSize: '13px', fontFamily: 'monospace', color: 'var(--text-primary)', wordBreak: 'break-all' }}>
                            {credAccountSid}
                        </code>
                        <button
                            onClick={() => copyValue(credAccountSid, 'acctSid')}
                            title="Copy Account SID"
                            aria-label="Copy Account SID"
                            style={{
                                display: 'flex', alignItems: 'center', gap: '5px',
                                padding: '5px 10px', border: '1px solid var(--border-color)',
                                borderRadius: 'var(--radius-sm)', background: copiedField === 'acctSid' ? 'var(--success-soft)' : 'var(--bg-secondary)',
                                color: copiedField === 'acctSid' ? 'var(--success)' : 'var(--text-primary)',
                                fontSize: '12px', fontWeight: 500, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit',
                            }}
                        >
                            {copiedField === 'acctSid' ? <><FiCheck size={13} /> Copied!</> : <><FiCopy size={13} /> Copy</>}
                        </button>
                    </div>
                </div>

                {/* API Key (secret) */}
                <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-primary)' }}>
                        API Key
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            padding: '1px 8px', borderRadius: '100px', fontSize: '11px', fontWeight: 600,
                            background: 'var(--warning-soft)', color: 'var(--warning)',
                        }}>
                            <FiLock size={10} /> Secret
                        </span>
                    </label>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)',
                        borderRadius: 'var(--radius-md)', padding: '10px 12px',
                    }}>
                        <FiKey size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                        <code style={{ flex: 1, fontSize: '13px', fontFamily: 'monospace', color: 'var(--text-secondary)', wordBreak: 'break-all' }}>
                            {showCredKey ? credApiKey : '•'.repeat(32)}
                        </code>
                        <button
                            onClick={() => setShowCredKey(!showCredKey)}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: '30px', height: '30px', border: 'none', background: 'transparent',
                                color: 'var(--text-secondary)', cursor: 'pointer', borderRadius: 'var(--radius-sm)', flexShrink: 0,
                            }}
                            title={showCredKey ? 'Hide key' : 'Show key'}
                            aria-label={showCredKey ? 'Hide API key' : 'Show API key'}
                        >
                            {showCredKey ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                        </button>
                        <button
                            onClick={() => copyValue(credApiKey, 'apiKey')}
                            title="Copy API Key"
                            aria-label="Copy API Key"
                            style={{
                                display: 'flex', alignItems: 'center', gap: '5px',
                                padding: '5px 10px', border: '1px solid var(--border-color)',
                                borderRadius: 'var(--radius-sm)', background: copiedField === 'apiKey' ? 'var(--success-soft)' : 'var(--bg-secondary)',
                                color: copiedField === 'apiKey' ? 'var(--success)' : 'var(--text-primary)',
                                fontSize: '12px', fontWeight: 500, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit',
                            }}
                        >
                            {copiedField === 'apiKey' ? <><FiCheck size={13} /> Copied!</> : <><FiCopy size={13} /> Copy</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default StAuthKeys;
